import { StorageProvider } from './storageTypes';
import { supabase, TABLES } from './supabaseClient';
import { SessionData } from '../hooks/useStatistics';

export class SupabaseStorageProvider implements StorageProvider {
  constructor(private userId: string) {}

  async getItem<T>(key: string): Promise<T | null> {
    try {
      switch (key) {
        case 'deletoSessions':
          return await this.getSessions() as T;
        
        case 'inactivityTimeout':
        case 'hideInactivitySeconds':
        case 'getdeleto-theme':
        case 'hasVisitedBefore':
          return await this.getPreference<T>(key);
        
        default:
          console.warn(`Unknown key for Supabase storage: ${key}`);
          return null;
      }
    } catch (error) {
      console.error(`Error getting item ${key} from Supabase:`, error);
      return null;
    }
  }

  async setItem<T>(key: string, value: T): Promise<boolean> {
    try {
      switch (key) {
        case 'deletoSessions':
          return await this.saveSessions(value as SessionData[]);
        
        case 'inactivityTimeout':
        case 'hideInactivitySeconds':
        case 'getdeleto-theme':
        case 'hasVisitedBefore':
          return await this.setPreference(key, value);
        
        default:
          console.warn(`Unknown key for Supabase storage: ${key}`);
          return false;
      }
    } catch (error) {
      console.error(`Error setting item ${key} in Supabase:`, error);
      return false;
    }
  }

  async removeItem(key: string): Promise<boolean> {
    try {
      switch (key) {
        case 'deletoSessions':
          return await this.clearSessions();
        
        case 'inactivityTimeout':
        case 'hideInactivitySeconds':
        case 'getdeleto-theme':
        case 'hasVisitedBefore':
          return await this.removePreference(key);
        
        default:
          console.warn(`Unknown key for Supabase storage: ${key}`);
          return false;
      }
    } catch (error) {
      console.error(`Error removing item ${key} from Supabase:`, error);
      return false;
    }
  }

  async clear(): Promise<boolean> {
    try {
      const [sessionsCleared, preferencesCleared] = await Promise.all([
        this.clearSessions(),
        this.clearPreferences()
      ]);
      
      return sessionsCleared && preferencesCleared;
    } catch (error) {
      console.error('Error clearing Supabase data:', error);
      return false;
    }
  }

  // Session-specific methods
  private async getSessions(): Promise<SessionData[]> {
    console.log(`📥 Fetching sessions from Supabase for user: ${this.userId}`);
    
    // First, test authentication by checking current user
    const { data: authUser, error: authError } = await supabase.auth.getUser();
    console.log('🔐 Current auth user:', { 
      userId: authUser?.user?.id, 
      authError: authError?.message,
      matches: authUser?.user?.id === this.userId 
    });
    
    if (!authUser?.user || authUser.user.id !== this.userId) {
      console.error('❌ Authentication mismatch or no user:', {
        expectedUserId: this.userId,
        actualUserId: authUser?.user?.id
      });
      return [];
    }

    // Test a simple count query first
    console.log('🧪 Testing simple count query...');
    const { count, error: countError } = await supabase
      .from(TABLES.USER_SESSIONS)
      .select('*', { count: 'exact', head: true })
      .eq('user_id', this.userId);
      
    if (countError) {
      console.error('❌ Count query failed:', countError);
      return [];
    }
    
    console.log(`📊 Found ${count} sessions for user`);
    
    if (count === 0) {
      console.log('ℹ️ No sessions found for user');
      return [];
    }
    
    // Now try the full query
    console.log('📋 Fetching full session data...');
    const { data, error } = await supabase
      .from(TABLES.USER_SESSIONS)
      .select('*')
      .eq('user_id', this.userId)
      .order('date', { ascending: false });

    if (error) {
      console.error('❌ Error fetching sessions from Supabase:', error);
      return [];
    }

    console.log(`✅ Successfully fetched ${data?.length || 0} sessions from Supabase`);

    // Transform database format to app format
    return data.map(row => ({
      id: row.session_id,
      date: row.date,
      wordCount: row.word_count,
      charCount: row.char_count,
      sessionDuration: row.session_duration,
      deletedChars: row.deleted_chars,
      deletedWords: row.deleted_words,
      numTimesDeleto: row.num_times_deleto,
      sessionEndedEarly: row.session_ended_early,
      sessionCompleted: row.session_completed
    }));
  }

  private async saveSessions(sessions: SessionData[]): Promise<boolean> {
    try {
      console.log(`💾 Saving ${sessions.length} sessions to Supabase for user: ${this.userId}`);
      
      // First, clear existing sessions for this user
      console.log('🗑️ Clearing existing sessions...');
      const { error: deleteError } = await supabase
        .from(TABLES.USER_SESSIONS)
        .delete()
        .eq('user_id', this.userId);
        
      if (deleteError) {
        console.error('❌ Error clearing existing sessions:', deleteError);
        return false;
      }

      // Then insert all sessions
      if (sessions.length > 0) {
        console.log(`📝 Inserting ${sessions.length} sessions...`);
        const { error } = await supabase
          .from(TABLES.USER_SESSIONS)
          .insert(sessions.map(session => ({
            user_id: this.userId,
            session_id: session.id,
            date: session.date,
            word_count: session.wordCount,
            char_count: session.charCount,
            session_duration: session.sessionDuration || 0,
            deleted_chars: session.deletedChars || 0,
            deleted_words: session.deletedWords || 0,
            num_times_deleto: session.numTimesDeleto || 0,
            session_ended_early: session.sessionEndedEarly || false,
            session_completed: session.sessionCompleted || false
          })));

        if (error) {
          console.error('❌ Error inserting sessions:', error);
          return false;
        }
        
        console.log(`✅ Successfully saved ${sessions.length} sessions to Supabase`);
      } else {
        console.log('ℹ️ No sessions to save');
      }

      return true;
    } catch (error) {
      console.error('❌ Error in saveSessions:', error);
      return false;
    }
  }

  private async clearSessions(): Promise<boolean> {
    const { error } = await supabase
      .from(TABLES.USER_SESSIONS)
      .delete()
      .eq('user_id', this.userId);

    if (error) {
      console.error('Error clearing sessions:', error);
      return false;
    }

    return true;
  }

  // Preference-specific methods
  private async getPreference<T>(key: string): Promise<T | null> {
    const { data, error } = await supabase
      .from(TABLES.USER_PREFERENCES)
      .select('preferences_data, inactivity_timeout, hide_inactivity_seconds, theme, has_visited_before')
      .eq('user_id', this.userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows returned
        return null;
      }
      console.error('Error fetching preferences:', error);
      return null;
    }

    // Map database columns to keys
    switch (key) {
      case 'inactivityTimeout':
        return data.inactivity_timeout as T;
      case 'hideInactivitySeconds':
        return data.hide_inactivity_seconds as T;
      case 'getdeleto-theme':
        return data.theme as T;
      case 'hasVisitedBefore':
        return data.has_visited_before as T;
      default:
        // Check in preferences_data JSONB column
        return data.preferences_data?.[key] || null;
    }
  }

  private async setPreference<T>(key: string, value: T): Promise<boolean> {
    try {
      // Prepare the update data based on the key
      let updateData: any = {};

      switch (key) {
        case 'inactivityTimeout':
          updateData.inactivity_timeout = value;
          break;
        case 'hideInactivitySeconds':
          updateData.hide_inactivity_seconds = value;
          break;
        case 'getdeleto-theme':
          updateData.theme = value;
          break;
        case 'hasVisitedBefore':
          updateData.has_visited_before = value;
          break;
        default:
          // Store in preferences_data JSONB column
          const currentPrefs = await this.getPreference('preferences_data') as Record<string, any> || {};
          updateData.preferences_data = { ...currentPrefs, [key]: value };
      }

      const { error } = await supabase
        .from(TABLES.USER_PREFERENCES)
        .upsert({
          user_id: this.userId,
          ...updateData,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error setting preference:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in setPreference:', error);
      return false;
    }
  }

  private async removePreference(key: string): Promise<boolean> {
    // For removing preferences, we set them to null
    return await this.setPreference(key, null);
  }

  private async clearPreferences(): Promise<boolean> {
    const { error } = await supabase
      .from(TABLES.USER_PREFERENCES)
      .delete()
      .eq('user_id', this.userId);

    if (error) {
      console.error('Error clearing preferences:', error);
      return false;
    }

    return true;
  }
} 